import { useState, useEffect, useCallback } from "react";
import { io } from "socket.io-client";
import { toast } from "../components/app-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  addRealtimeNotification,
  markAsReadLocally,
  markNotificationRead,
} from "../slice/notifications/notificationsSlice";
import { addPharmacyRealtimeNotification } from "../slice/pharmacy/notifications/pharmacyNotificationsSlice";
import { addIncomingMessage } from "../slice/pharmacyChatOrders/pharmacyChatOrdersSlice";
import { getUserNotifications } from "../reducer/notifications/notificationsReducer";
import notificationSound from "../assets/notification/alert1.mp3";
import { getPatientReportNavigation } from "../utils/patientReportNotification";
import {
  isOperationRequestNotification,
  operationRequestNotificationRoute,
  operationRequestNotificationText,
} from "../utils/operationRequestNotification";
import i18n from "../i18n";
import {
  ensureFreshAccessToken,
  getAccessToken,
  getTokenUserId,
  isAuthError,
} from "../utils/socketAuth";
import { CHAT_SOCKET_CONNECTED_EVENT } from "../utils/chatEvents";

const SOCKET_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

// GLOBAL duplicate prevention - shared across all hook instances
// This prevents duplicate toasts when multiple components use the hook
const globalShownNotificationIds = new Set();

// Pre-load notification sound for better reliability
let notificationAudio = null;
let audioUnlocked = false;

const initAudio = () => {
  if (!notificationAudio) {
    notificationAudio = new Audio(notificationSound);
    notificationAudio.volume = 0.5;
    notificationAudio.preload = "auto";
  }
};

// Unlock audio on first user interaction (required by browser autoplay policy)
const unlockAudio = () => {
  if (audioUnlocked) return;
  initAudio();
  // Play and immediately pause to unlock audio context
  const playPromise = notificationAudio.play();
  if (playPromise) {
    playPromise
      .then(() => {
        notificationAudio.pause();
        notificationAudio.currentTime = 0;
        audioUnlocked = true;
      })
      .catch(() => {});
  }
};

// Add unlock listeners once
if (typeof window !== "undefined") {
  ["click", "touchstart", "keydown"].forEach((event) => {
    document.addEventListener(event, unlockAudio, { once: true });
  });
}

// Play notification alert sound
const playNotificationSound = () => {
  try {
    initAudio();
    notificationAudio.currentTime = 0;
    notificationAudio
      .play()
      .catch((err) =>
        console.warn("[SOCKET_CLIENT] Could not play notification sound:", err),
      );
  } catch (err) {
    console.warn("[SOCKET_CLIENT] Error playing notification audio:", err);
  }
};

// ---------------------------------------------------------------------------------------------
// ONE notification socket per browser tab, shared by every mounted dashboard layout.
//
// Every page renders its own layout (doctor and pharmacy), so a socket owned by the hook was torn
// down and re-handshaken on each navigation: messages emitted during that gap never arrived, and
// a socket still connecting at unmount leaked with its listeners. The socket now lives at module
// level; the hook only takes and releases a reference, and the socket is closed a few seconds
// after the last layout unmounts (logout, or leaving the dashboard), not on every page change.
// ---------------------------------------------------------------------------------------------
const RELEASE_DELAY_MS = 3000;
const MAX_RETRY_DELAY_MS = 30000;
const shared = {
  socket: null,
  userId: null,
  users: 0,
  releaseTimer: null,
  retryTimer: null,
  retries: 0,
};
// Latest React context for the socket handlers, which outlive any single layout mount.
const ctx = { dispatch: null, navigate: null };

const handleNotification = (notificationData) => {
  console.log("[SOCKET_CLIENT] ========================================");
  console.log("[SOCKET_CLIENT] Received notification:", notificationData);
  console.log(
    "[SOCKET_CLIENT] Notification type:",
    notificationData.notification_type,
  );
  console.log(
    "[SOCKET_CLIENT] Notification ID:",
    notificationData.notification_id || notificationData.id,
  );
  console.log("[SOCKET_CLIENT] ========================================");

  // Check if this notification was already shown (prevent duplicate toasts)
  // Using GLOBAL Set to prevent duplicates across all hook instances
  const notificationId =
    notificationData.notification_id || notificationData.id;
  const isAlreadyShown = globalShownNotificationIds.has(notificationId);

  if (isAlreadyShown) {
    console.log(
      "[SOCKET_CLIENT] ⚠️ Duplicate notification detected (GLOBAL check), skipping toast:",
      notificationId,
    );
    // Still dispatch to Redux (it handles duplicates), but don't show toast again
    return;
  }

  // Mark this notification as shown in GLOBAL Set
  if (notificationId) {
    globalShownNotificationIds.add(notificationId);
    console.log(
      "[SOCKET_CLIENT] ✅ Added notification to global duplicate prevention:",
      notificationId,
    );
    // Clean up old notification IDs after 1 minute to prevent memory leak
    setTimeout(() => {
      globalShownNotificationIds.delete(notificationId);
      console.log(
        "[SOCKET_CLIENT] 🧹 Cleaned up notification from global set:",
        notificationId,
      );
    }, 60000);
  }

  // Check if this is a pharmacy chat message
  const isChatMessage =
    notificationData.notification_type === "PHARMACY_CHAT_MESSAGE";

  if (isChatMessage) {
    console.log(
      "[SOCKET_CLIENT] Dispatching incoming chat message to pharmacyChatOrders slice",
    );
    ctx.dispatch?.(addIncomingMessage(notificationData.data || notificationData));
    playNotificationSound();
    toast.info(i18n.t("notifications.newMessageFromAdmin"), {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
    });
    return;
  }

  // Check if this is a pharmacy notification based on notification_type.
  // NOTE: the pharmacy "payment received" notice carries the exact type
  // `PAYMENT_RECEIVED` (no ORDER/PHARMACY substring), so it is matched
  // explicitly. Doctors never receive plain `PAYMENT_RECEIVED` — they get
  // `PAYMENT_SUCCESSFUL` / `APPOINTMENT_PAYMENT_RECEIVED` — so this stays
  // pharmacy-only and won't misroute doctor payments.
  const isPharmacyNotification =
    notificationData.notification_type &&
    (notificationData.notification_type.includes("ORDER") ||
      notificationData.notification_type.includes("PHARMACY") ||
      notificationData.notification_type.includes("PRESCRIPTION") ||
      notificationData.notification_type === "NEW_ORDER_RECEIVED" ||
      notificationData.notification_type === "PAYMENT_RECEIVED");

  // Doctor payment notifications carry `PAYMENT_SUCCESSFUL` (not `PAYMENT_RECEIVED`),
  // so they fall through to the regular notifications slice above — never the
  // pharmacy slice.

  console.log(
    "[SOCKET_CLIENT] Is pharmacy notification?",
    isPharmacyNotification,
  );

  // Check if this is a consultation fee notification
  const isConsultationFeeNotification =
    notificationData.notification_type &&
    (notificationData.notification_type === "CONSULTATION_FEE_APPROVED" ||
      notificationData.notification_type === "CONSULTATION_FEE_REJECTED");

  // Check if this is a settlement (pay in / pay out) decision notification
  const isSettlementNotification =
    notificationData.notification_type &&
    (notificationData.notification_type === "SETTLEMENT_APPROVED" ||
      notificationData.notification_type === "SETTLEMENT_REJECTED" ||
      notificationData.notification_type === "SETTLEMENT_CANCELLED" ||
      notificationData.notification_type === "SETTLEMENT_COMPLETED");

  if (isPharmacyNotification) {
    // Dispatch pharmacy notification to pharmacy slice
    console.log(
      "[SOCKET_CLIENT] Dispatching pharmacy notification to pharmacy slice",
    );
    ctx.dispatch?.(addPharmacyRealtimeNotification(notificationData));
    playNotificationSound();

    // Show toast notification for pharmacy notifications
    toast.info(
      notificationData.body ||
        notificationData.message ||
        i18n.t("notifications.toast.newPharmacyNotification"),
      {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      },
    );
  } else {
    // Dispatch regular notification to notifications slice
    console.log(
      "[SOCKET_CLIENT] Dispatching regular notification to notifications slice",
    );
    console.log(
      "[SOCKET_CLIENT] Notification data being dispatched:",
      JSON.stringify(notificationData, null, 2),
    );
    ctx.dispatch?.(addRealtimeNotification(notificationData));

    // Chat messages are announced by their `new_message` event in the dashboard layouts
    // (sidebar Chat badge + toast for admin messages, toast + dock for patient messages). The
    // CHAT_MESSAGE notification only lands in the bell; a sound/toast here as well doubled
    // every chat message.
    const isChatNotification =
      notificationData.notification_type === "CHAT_MESSAGE";
    if (!isChatNotification) playNotificationSound();
    let toastMessage;
    if (isOperationRequestNotification(notificationData)) {
      // MCC 2 status change of the doctor's operation request. The socket carries only the
      // English text, so the message is built in the app language from the payload.
      toastMessage = operationRequestNotificationText(notificationData).body;
    } else {
      toastMessage =
        notificationData.body ||
        notificationData.message ||
        i18n.t("notifications.toast.newNotification");
    }

    // A shared lab / imaging / nursing / physiotherapy report: clicking the toast opens the
    // patient's reports with that report highlighted, and marks the notification read —
    // the same outcome as clicking it in the notifications panel.
    const reportNavigation = getPatientReportNavigation(notificationData);
    // Operation request update: clicking the toast opens the appointment it belongs to.
    const operationRequestRoute = isOperationRequestNotification(notificationData)
      ? operationRequestNotificationRoute(notificationData)
      : null;
    const reportToastOptions = operationRequestRoute
      ? {
          title: operationRequestNotificationText(notificationData).title,
          autoClose: 8000,
          onClick: () => {
            const markId = notificationData.notification_id;
            if (markId) {
              ctx.dispatch?.(markAsReadLocally(markId));
              ctx.dispatch?.(markNotificationRead(markId));
            }
            ctx.navigate?.(operationRequestRoute);
          },
        }
      : reportNavigation
      ? {
          title: notificationData.title || undefined,
          autoClose: 8000,
          onClick: () => {
            const markId = notificationData.notification_id;
            if (markId) {
              ctx.dispatch?.(markAsReadLocally(markId));
              ctx.dispatch?.(markNotificationRead(markId));
            }
            ctx.navigate?.(reportNavigation.to, {
              state: reportNavigation.state,
            });
          },
        }
      : {};

    // Show toast notification
    if (!isChatNotification) {
      toast.info(toastMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        ...reportToastOptions,
      });
    }

    // If this is a consultation fee notification, trigger a custom event
    // that the MyProfile component can listen to for refreshing data
    if (isConsultationFeeNotification) {
      console.log(
        "[SOCKET_CLIENT] Triggering consultation fee refresh event",
      );
      window.dispatchEvent(
        new CustomEvent("consultation_fee_updated", {
          detail: notificationData,
        }),
      );
    }

    // If this is a settlement decision notification, trigger a custom event so the
    // wallet / settlement pages can refresh their data in real time.
    if (isSettlementNotification) {
      console.log(
        "[SOCKET_CLIENT] Triggering settlement update refresh event",
      );
      window.dispatchEvent(
        new CustomEvent("settlement_updated", {
          detail: notificationData,
        }),
      );
    }
  }

  // Silent refresh: dispatch a global event so any active page can refresh its data
  window.dispatchEvent(
    new CustomEvent("notification_received_refresh", {
      detail: {
        notification_type: notificationData.notification_type,
        data: notificationData.data,
      },
    }),
  );
};

// Reconnect a socket that socket.io will not reconnect by itself: the gateway refused the
// handshake (expired JWT) or closed the connection. Refreshes the token first when needed.
const scheduleManualReconnect = (socket, { authRejected = false } = {}) => {
  if (socket !== shared.socket || shared.retryTimer) return;
  const delay = Math.min(MAX_RETRY_DELAY_MS, 1000 * 2 ** shared.retries);
  shared.retries += 1;
  shared.retryTimer = setTimeout(async () => {
    shared.retryTimer = null;
    if (socket !== shared.socket || socket.connected) return;
    const rejected = authRejected ? socket.io.opts.query?.token : null;
    const token = await ensureFreshAccessToken(rejected);
    // No session any more (logout) or another user signed in: stop here.
    if (!token || socket !== shared.socket) return;
    if (String(getTokenUserId(token)) !== String(shared.userId)) return;
    socket.io.opts.query = { token };
    socket.connect();
  }, delay);
};

const createSocket = (token) => {
  console.log("[SOCKET_CLIENT] Initializing shared notification socket...");
  const socket = io(SOCKET_URL, {
    query: { token },
    transports: ["websocket", "polling"],
    reconnection: true,
    // Never give up on transport failures: a dead notification socket silently stops chats,
    // badges and the dock until the next full page load.
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    timeout: 20000,
    forceNew: true,
  });

  socket.on("connect", () => {
    console.log("[SOCKET_CLIENT] Socket connected:", socket.id);
    shared.retries = 0;
    // Anything may have changed while the socket was down: let unread badges re-sync.
    window.dispatchEvent(new CustomEvent(CHAT_SOCKET_CONNECTED_EVENT));
    window.dispatchEvent(
      new CustomEvent("chat-unread-refresh", { detail: {} }),
    );
  });

  socket.on("disconnect", (reason) => {
    console.log("[SOCKET_CLIENT] Socket disconnected:", reason);
    // Server-side close: socket.io does not reconnect by itself.
    if (reason === "io server disconnect") scheduleManualReconnect(socket);
  });

  socket.on("connect_error", (error) => {
    console.error(
      "[SOCKET_CLIENT] Socket connection error:",
      error?.message || error,
    );
    // `active` is false when the gateway refused the handshake (auth middleware); socket.io
    // then waits for a manual connect(). Transport errors keep it active and retry by themselves.
    if (!socket.active) {
      scheduleManualReconnect(socket, { authRejected: isAuthError(error) });
    }
  });

  // Each automatic reconnect re-sends the handshake: use the newest stored token, not the one
  // the socket was created with (the app refreshes it in the background).
  socket.io.on("reconnect_attempt", () => {
    const latest = getAccessToken();
    if (latest) socket.io.opts.query = { token: latest };
  });

  socket.on("notification", handleNotification);

  // `new_message` (chat) is handled by the dashboard layouts on the returned socket.

  socket.on("connected", (data) => {
    console.log("[SOCKET_CLIENT] Server confirmed connection:", data?.user_id);
  });

  socket.on("error", (error) => {
    console.error("[SOCKET_CLIENT] Socket error:", error);
  });

  return socket;
};

export const disposeSharedSocket = () => {
  if (shared.releaseTimer) clearTimeout(shared.releaseTimer);
  if (shared.retryTimer) clearTimeout(shared.retryTimer);
  shared.releaseTimer = null;
  shared.retryTimer = null;
  const socket = shared.socket;
  shared.socket = null;
  shared.userId = null;
  shared.retries = 0;
  if (socket) {
    try {
      socket.removeAllListeners();
      socket.disconnect();
    } catch (e) {}
  }
};

const acquireSharedSocket = () => {
  const token = getAccessToken();
  if (!token) return null;
  const userId = getTokenUserId(token);
  if (shared.releaseTimer) {
    clearTimeout(shared.releaseTimer);
    shared.releaseTimer = null;
  }
  // Another account signed in on this tab (doctor <-> pharmacy): never reuse the previous
  // user's socket.
  if (shared.socket && String(shared.userId) !== String(userId)) {
    disposeSharedSocket();
  }
  if (!shared.socket) {
    shared.socket = createSocket(token);
    shared.userId = userId;
  } else if (!shared.socket.connected && !shared.socket.active) {
    // Refused earlier and not retrying: try again with the current token.
    shared.socket.io.opts.query = { token };
    shared.socket.connect();
  }
  shared.users += 1;
  return shared.socket;
};

const releaseSharedSocket = () => {
  shared.users = Math.max(0, shared.users - 1);
  if (shared.users > 0 || shared.releaseTimer) return;
  // A navigation unmounts one page's layout and mounts the next a moment later: keep the
  // connection across that hand-over and close it only if no layout comes back.
  shared.releaseTimer = setTimeout(() => {
    shared.releaseTimer = null;
    if (shared.users === 0) disposeSharedSocket();
  }, RELEASE_DELAY_MS);
};

export const useSocketNotifications = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notifications: reduxNotifications } = useSelector(
    (state) => state.notifications,
  );
  const [socket, setSocket] = useState(() => shared.socket);
  const [isConnected, setIsConnected] = useState(
    () => !!shared.socket?.connected,
  );

  // The socket handlers always use the current dispatch / navigate without reconnecting.
  useEffect(() => {
    ctx.dispatch = dispatch;
    ctx.navigate = navigate;
  }, [dispatch, navigate]);

  useEffect(() => {
    const s = acquireSharedSocket();
    if (!s) {
      console.warn("No JWT token found for socket connection");
      return undefined;
    }
    setSocket(s);
    setIsConnected(s.connected);
    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);
    s.on("connect", onConnect);
    s.on("disconnect", onDisconnect);
    return () => {
      s.off("connect", onConnect);
      s.off("disconnect", onDisconnect);
      releaseSharedSocket();
    };
  }, []);

  // Load notifications from API on mount
  useEffect(() => {
    const token = getAccessToken();
    if (token && !reduxNotifications.length) {
      // Load initial notifications from API
      dispatch(getUserNotifications({ page: 1, limit: 20 }));
    }
  }, [dispatch, reduxNotifications.length]);

  const connectSocket = useCallback(() => {
    const s = shared.socket;
    if (s && !s.connected) s.connect();
  }, []);

  const disconnectSocket = useCallback(() => {
    disposeSharedSocket();
    setSocket(null);
    setIsConnected(false);
  }, []);

  // Notifications are managed by Redux; kept for API compatibility.
  const clearNotifications = useCallback(() => {}, []);
  const removeNotification = useCallback(() => {}, []);
  const markAsRead = useCallback(() => {}, []);

  return {
    socket,
    isConnected,
    notifications: reduxNotifications, // Use Redux notifications
    connectSocket,
    disconnectSocket,
    clearNotifications,
    removeNotification,
    markAsRead,
  };
};

export default useSocketNotifications;
